import React from 'react';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  Atom,
  Plugins,
  StyleAttributeImm,
  AtomRenderProps,
} from '@packages/systems/wfdl';
import {percent, px} from '@packages/systems/core/utils/WebflowCssSubset';
import {setIn} from '@packages/systems/core/utils/ImmUtils';
import {WebflowEnvironments} from '@packages/systems/core-constants';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {useRuntimeEnvironment} from '@packages/systems/render';

const setDisplayNone = setIn(['style', 'display'], 'none');

const {DESIGNER, SSR} = WebflowEnvironments;

// @ts-expect-error - TS7006 - Parameter 'pageNumber' implicitly has an 'any' type. | TS7006 - Parameter 'totalPages' implicitly has an 'any' type.
const createCountText = (pageNumber, totalPages) => {
  return pageNumber > totalPages || (pageNumber === 1 && totalPages === 1)
    ? ''
    : `${Math.max(pageNumber, 1)} / ${String(totalPages)}`;
};

const maybeAddAriaLabel = (
  ariaLabel: string | StyleAttributeImm | Map<string, string>,
  // @ts-expect-error - TS7006 - Parameter 'pageNumber' implicitly has an 'any' type.
  pageNumber,
  // @ts-expect-error - TS7006 - Parameter 'totalPages' implicitly has an 'any' type.
  totalPages
) => {
  return ariaLabel
    ? ariaLabel
    : `Page ${Math.max(pageNumber, 1)} of ${String(totalPages)}`;
};

const maybeAddRole = (
  role: string | StyleAttributeImm | Map<string, string>
) => {
  return role ? role : 'heading';
};

const PaginationCount = Atom.createRender(
  ({context, attributes, classes, generateAttributes}: AtomRenderProps) => {
    const runtimeEnvironment = useRuntimeEnvironment();
    const pageNumber = context.get('pageNumber', 1);
    const totalPages = context.get('totalPages', 1);

    const countText = createCountText(pageNumber, totalPages);
    const newAttributes = attributes
      .update('aria-label', (ariaLabel) =>
        // @ts-expect-error - TS7006 - Parameter 'ariaLabel' implicitly has an 'any' type.
        maybeAddAriaLabel(ariaLabel, pageNumber, totalPages)
      )
      // @ts-expect-error - TS7006 - Parameter 'role' implicitly has an 'any' type.
      .update('role', maybeAddRole);

    if (countText) {
      return (
        <div {...generateAttributes(newAttributes, classes)}>{countText}</div>
      );
    }

    switch (runtimeEnvironment) {
      case DESIGNER: {
        // In the Designer, we want to render the div with display none, so that the Designer canvas
        // would still contain the native DOM node, which is needed for the Designer UI and drag functionality.
        return (
          <div {...generateAttributes(setDisplayNone(newAttributes), classes)}>
            {countText}
          </div>
        );
      }
      case SSR: {
        // In the live site, we don't render anything so that the HTML would not contain anything extra.
        return null;
      }
      default: {
        // In all the other cases, i.e. in EXPORT, we want to render the div even when the countText is
        // blank so that the exported HTML contains the element.
        return (
          <div {...generateAttributes(newAttributes, classes)}>{countText}</div>
        );
      }
    }
  }
);

const config = createIntegratedAtom({
  displayName: 'Page Count',
  className: 'w-page-count',
  style: {
    base: {
      main: {
        noPseudo: {
          width: percent(100),
          textAlign: 'center',
          marginTop: px(20),
        },
      },
    },
  },
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Text')),
  },
  showEmpty: false,
  render: PaginationCount,
  constraints: {
    ancestors: [
      {is: 'Pagination', rule: 'ExactlyOne'},
      {is: 'PaginationPrevious', rule: 'Forbid'},
      {is: 'PaginationNext', rule: 'Forbid'},
    ],
    descendants: [
      {
        is: {check: () => true, name: 'Element'},
        rule: 'Forbid',
      },
    ],
  },
  fields: {},
});

export default config;
